import {
  Colorize,
  DeveloperBoard,
  DeveloperBoardOff,
  DoneOutline,
  GridOff,
  GridOn,
  MoreVert,
} from '@mui/icons-material'
import { Box, IconButton, Menu, MenuItem, Theme, Tooltip, useMediaQuery } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import {
  APPLICATION_MODES,
  APPLYTOALL_ACTIVE,
  getApplicationMode,
  getApplyToAllActive,
  getTopicType,
} from './renderer/main'
import { ToolBarContainer, ToolBarIcon, ToolBarWrapper, StyledSlider } from './__styled'
import SplitIcon from '@/assets/svgs/split.svg'
import CombineIcon from '@/assets/svgs/combine.svg'
import RotateIcon from '@/assets/svgs/rotate.svg'
import UndoIcon from '@/assets/svgs/undo.svg'
import RedoIcon from '@/assets/svgs/redo.svg'
import SVG from 'react-inlinesvg'
import { useState, useImperativeHandle, forwardRef } from 'react'

interface ToolBarProps {
  mode: number
  bordered: boolean
  debugMode: boolean
  onPressBack: () => void
  onPressForward: () => void
  onPressMerge: () => void
  onPressSplit: () => void
  onPressRotate: () => void
  onPressAll: () => void
  onPressColorPick: () => void
  onPressGrid: () => void
  onPressInspector: () => void
  onPressFullMode: () => void
  onSliderAccentScaleChange: (value: number) => void
  onSliderAccentNumberChange: (value: number) => void
  onSliderAccentRotationChange: (value: number) => void
  onSliderPrintTilingChange: (value: number) => void
  onCameraHorizontalPositionChange: (value: number) => void
  onCameraZoomInPressed: () => void
  onCameraZoomOutPressed: () => void
  isSliderVisible: boolean
  applyAll: boolean
  onPressReset: () => void
  onPressHide: () => void
}

export default forwardRef(function PatternToolBar(
  {
    mode,
    bordered,
    debugMode,
    onPressBack,
    onPressForward,
    onPressMerge,
    onPressSplit,
    onPressRotate,
    onPressAll,
    onPressColorPick,
    onPressGrid,
    onPressInspector,
    onSliderAccentScaleChange,
    onSliderAccentNumberChange,
    onSliderAccentRotationChange,
    onSliderPrintTilingChange,
    onCameraHorizontalPositionChange,
    onCameraZoomOutPressed,
    onCameraZoomInPressed,
    isSliderVisible,
    applyAll,
    onPressReset,
    onPressHide, // sliderValue,
    // onPressFullMode,
  }: ToolBarProps,
  ref,
) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [sliderAccentScale, setSliderAccentScale] = useState<number>(100)
  const [sliderAccentNumber, setSliderAccentNumber] = useState<number>(1)
  const [sliderAccentRotation, setSliderAccentRotation] = useState<number>(0)
  const [sliderPrintTiling, setSliderPrintTiling] = useState<number>(1)
  const [cameraHorizontalPosition, setCameraHorizontalPosition] = useState<number>(0.5)
  const open = Boolean(anchorEl)
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down(1350))

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  // const changeSliderValue = (value: number) => {
  //   setSliderValue(value)
  // }

  useImperativeHandle(
    ref,
    () => ({
      changeAccentScaleSliderValue(value: number) {
        setSliderAccentScale(value)
        onSliderAccentScaleChange(value)
      },
      changeAccentNumberSliderValue(value: number) {
        setSliderAccentNumber(value)
        onSliderAccentNumberChange(value)
      },
      changeAccentRotationSliderValue(value: number) {
        setSliderAccentRotation(value)
        onSliderAccentRotationChange(value)
      },
      changePrintTilingSliderValue(value: number) {
        setSliderPrintTiling(value)
        onSliderPrintTilingChange(value)
      },

      changeCameraHorizontalSliderValue(value: number) {
        setCameraHorizontalPosition(value)
        onCameraHorizontalPositionChange(value)
      },
    }),
    [],
  )

  const handleSliderAccentScaleChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      newValue = newValue[0]
    }
    setSliderAccentScale(newValue)
    onSliderAccentScaleChange(newValue)
  }

  const handleSliderAccentNumberChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      newValue = newValue[0]
    }
    setSliderAccentNumber(newValue)
    onSliderAccentNumberChange(newValue)
  }

  const handleSliderRotationNumberChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      newValue = newValue[0]
    }
    setSliderAccentRotation(newValue)
    onSliderAccentRotationChange(newValue)
  }

  const handleCameraHorizontalPositionChange = (event: Event, newValue: number | number[]) => {
    if (Array.isArray(newValue)) {
      newValue = newValue[0]
    }
    setCameraHorizontalPosition(newValue)
    onCameraHorizontalPositionChange(newValue)
  }

  const handleCameraZoomIn = () => {
    onCameraZoomInPressed()
  }

  const handleCameraZoomOut = () => {
    onCameraZoomOutPressed()
  }

  return (
    <>
      {isSmallScreen ? (
        <Box
          sx={{
            position: 'absolute',
            right: 350,
            top: 18,
            display: 'flex',
            zIndex: 10,
          }}
        >
          <IconButton
            aria-label='more'
            aria-controls='long-menu'
            aria-haspopup='true'
            onClick={handleMenuOpen}
          >
            <MoreVert sx={{ color: 'white' }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={open}
            onClose={handleMenuClose}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                backdropFilter: 'blur(16px)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                color: '#fff',
              },
              '& .MuiMenuItem-root': {
                padding: '8px 16px',
                borderRadius: '8px',
                margin: '4px 8px',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(99, 102, 241, 0.15)',
                },
              },
            }}
          >
            <MenuItem onClick={handleMenuClose}>
              <Tooltip title='Reset' arrow>
                <ToolBarIcon onClick={onPressReset}>
                  <SVG src={RotateIcon} width={20} height={20} />
                </ToolBarIcon>
              </Tooltip>
            </MenuItem>


            {getTopicType() !== 'gallery' && (
              <MenuItem onClick={handleMenuClose}>
                 <Tooltip title='Hide' arrow>
                      <ToolBarIcon onClick={onPressHide}>
                          {mode === APPLICATION_MODES.HIDE_SURFACES ? (
                              <DoneOutline sx={{ fontSize: 20 }} />
                          ) : (
                              <SVG src={CombineIcon} width={20} height={20} />
                          )}
                      </ToolBarIcon>
                  </Tooltip>
              </MenuItem>
            )}

            <MenuItem onClick={handleMenuClose}>
              <Tooltip title='Undo' arrow>
                <ToolBarIcon onClick={onPressBack}>
                  <SVG src={UndoIcon} width={20} height={20} />
                </ToolBarIcon>
              </Tooltip>
            </MenuItem>

            <MenuItem onClick={handleMenuClose}>
              <Tooltip title='Redo' arrow>
                <ToolBarIcon onClick={onPressForward}>
                  <SVG src={RedoIcon} width={20} height={20} />
                </ToolBarIcon>
              </Tooltip>
            </MenuItem>

            {getTopicType() !== 'gallery' && (
              <MenuItem onClick={handleMenuClose}>
                <Tooltip title='Merge' arrow>
                  <ToolBarIcon onClick={onPressMerge}>
                    {mode === APPLICATION_MODES.MERGE_SURFACES ? (
                      <DoneOutline sx={{ fontSize: 20 }} />
                    ) : (
                      <SVG src={CombineIcon} width={20} height={20} />
                    )}
                  </ToolBarIcon>
                </Tooltip>
              </MenuItem>
            )}

            {getTopicType() !== 'gallery' && (
              <MenuItem onClick={handleMenuClose}>
                <Tooltip title='Split' arrow>
                  <ToolBarIcon onClick={onPressSplit}>
                    {mode === APPLICATION_MODES.SPLIT_SURFACES ? (
                      <DoneOutline sx={{ fontSize: 20 }} />
                    ) : (
                      <SVG src={SplitIcon} width={20} height={20} />
                    )}
                  </ToolBarIcon>
                </Tooltip>
              </MenuItem>
            )}

            <MenuItem onClick={handleMenuClose}>
              <Tooltip title='Rotate' arrow>
                <ToolBarIcon onClick={onPressRotate}>
                  <SVG src={RotateIcon} width={20} height={20} />
                </ToolBarIcon>
              </Tooltip>
            </MenuItem>

            <MenuItem onClick={handleMenuClose}>
              <Tooltip title='Color Picker' arrow>
                <ToolBarIcon onClick={onPressColorPick}>
                  <Colorize
                    sx={{
                      fontSize: 20,
                      color:
                        mode === APPLICATION_MODES.PICKER_PICKING ||
                        mode === APPLICATION_MODES.PICKER_PICKED
                          ? '#6366f1'
                          : 'inherit',
                    }}
                  />
                </ToolBarIcon>
              </Tooltip>
            </MenuItem>

            {getTopicType() != 'gallery' && (
              <MenuItem onClick={handleMenuClose}>
                <Tooltip title='Border' arrow>
                  <ToolBarIcon onClick={onPressGrid}>
                    {bordered ? (
                      <GridOn sx={{ fontSize: 20 }} />
                    ) : (
                      <GridOff sx={{ fontSize: 20 }} />
                    )}
                  </ToolBarIcon>
                </Tooltip>
              </MenuItem>
            )}

            <MenuItem onClick={handleMenuClose}>
              <Tooltip title='Inspector' arrow>
                <ToolBarIcon onClick={onPressInspector}>
                  {debugMode ? (
                    <DeveloperBoard sx={{ fontSize: 20 }} />
                  ) : (
                    <DeveloperBoardOff sx={{ fontSize: 20 }} />
                  )}
                </ToolBarIcon>
              </Tooltip>
            </MenuItem>

            {(getApplicationMode() === 'print' ||
              getApplicationMode() === 'color' ||
              (getApplicationMode() === 'material' && getTopicType() === 'gallery')) && (
              <MenuItem>
                <Tooltip title='All' arrow>
                  <ToolBarIcon onClick={onPressAll}>
                    {(() => {
                      const isApplyMode =
                        (getApplicationMode() === 'print' ||
                          getApplicationMode() === 'color' ||
                          (getApplicationMode() === 'material' && getTopicType() === 'gallery')) &&
                        applyAll
                      return isApplyMode ? (
                        <DoneOutline sx={{ fontSize: 20 }} />
                      ) : (
                        <SVG src={SplitIcon} width={20} height={20} />
                      )
                    })()}
                  </ToolBarIcon>
                </Tooltip>
              </MenuItem>
            )}

            {/* {isSliderVisible && (
              <MenuItem>
                <Tooltip
                  sx={{ height: 15, width: 200, bgcolor: '#ffffff26' }}
                  title={`Accent scale: ${sliderAccentScale}`}
                >
                  <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
                    <Box color='white' fontSize='12px' mb={-1}>
                      Accent Scaling
                    </Box>
                    <StyledSlider
                      value={sliderAccentScale}
                      onChange={handleSliderAccentScaleChange}
                      min={0}
                      max={100}
                    />
                  </Box>
                </Tooltip>
              </MenuItem>
            )} */}

            {/* {isSliderVisible && (
              <MenuItem>
                <Tooltip
                  sx={{ height: 15, width: 200, bgcolor: '#ffffff26' }}
                  title={`Accent number: ${sliderAccentNumber}`}
                >
                  <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
                    <Box color='white' fontSize='12px' mb={-1}>
                      Accent Tiling
                    </Box>
                    <StyledSlider
                      value={sliderAccentNumber}
                      onChange={handleSliderAccentNumberChange}
                      min={1}
                      max={4}
                    />
                  </Box>
                </Tooltip>
              </MenuItem>
            )} */}

            {/* {isSliderVisible && (
              <MenuItem>
                <Tooltip
                  sx={{ height: 15, width: 200, bgcolor: '#ffffff26' }}
                  title={`Accent number: ${sliderAccentRotation}`}
                >
                  <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
                    <Box color='white' fontSize='12px' mb={-1}>
                      Accent Rotation
                    </Box>
                    <StyledSlider
                      value={sliderAccentRotation}
                      onChange={handleSliderRotationNumberChange}
                      min={0}
                      max={360}
                    />
                  </Box>
                </Tooltip>
              </MenuItem>
            )} */}
          </Menu>
        </Box>
      ) : (
        <ToolBarWrapper sx={{
            right: 250,
        }}>
          <ToolBarContainer>
            <Tooltip title='Reset' arrow>
              <ToolBarIcon onClick={onPressReset}>
                <SVG src={RotateIcon} width={20} height={20} />
              </ToolBarIcon>
            </Tooltip>

            <Tooltip title='Hide' arrow>
              <ToolBarIcon onClick={onPressHide}>
                {mode === APPLICATION_MODES.HIDE_SURFACES ? (
                  <DoneOutline sx={{ fontSize: 20 }} />
                ) : (
                  <SVG src={CombineIcon} width={20} height={20} />
                )}
              </ToolBarIcon>
            </Tooltip>
              
            {(getApplicationMode() === 'print' ||
              getApplicationMode() === 'color' ||
              (getApplicationMode() === 'material' && getTopicType() === 'gallery')) && (
              <Tooltip title='All' arrow>
                <ToolBarIcon onClick={onPressAll}>
                  {(() => {
                    const isApplyMode =
                      (getApplicationMode() === 'print' ||
                        getApplicationMode() === 'color' ||
                        (getApplicationMode() === 'material' && getTopicType() === 'gallery')) &&
                      applyAll

                    return isApplyMode ? (
                      <DoneOutline sx={{ fontSize: 20 }} />
                    ) : (
                      <SVG src={SplitIcon} width={20} height={20} />
                    )
                  })()}
                </ToolBarIcon>
              </Tooltip>
            )}
            <Tooltip title='Undo' arrow>
              <ToolBarIcon onClick={onPressBack}>
                <SVG src={UndoIcon} width={20} height={20} />
              </ToolBarIcon>
            </Tooltip>
            <Tooltip title='Redo' arrow>
              <ToolBarIcon onClick={onPressForward}>
                <SVG src={RedoIcon} width={20} height={20} />
              </ToolBarIcon>
            </Tooltip>

            {getTopicType() !== 'gallery' && (
              <Tooltip title='Merge' arrow>
                <ToolBarIcon onClick={onPressMerge}>
                  {mode === APPLICATION_MODES.MERGE_SURFACES ? (
                    <DoneOutline sx={{ fontSize: 20 }} />
                  ) : (
                    <SVG src={CombineIcon} width={20} height={20} />
                  )}
                </ToolBarIcon>
              </Tooltip>
            )}

            {getTopicType() !== 'gallery' && (
              <Tooltip title='Split' arrow>
                <ToolBarIcon onClick={onPressSplit}>
                  {mode === APPLICATION_MODES.SPLIT_SURFACES ? (
                    <DoneOutline sx={{ fontSize: 20 }} />
                  ) : (
                    <SVG src={SplitIcon} width={20} height={20} />
                  )}
                </ToolBarIcon>
              </Tooltip>
            )}

            <Tooltip title='Rotate' arrow>
              <ToolBarIcon onClick={onPressRotate}>
                {mode === APPLICATION_MODES.ROTATE_TILE ? (
                  <DoneOutline sx={{ fontSize: 20 }} />
                ) : (
                  <SVG src={RotateIcon} width={20} height={20} />
                )}
              </ToolBarIcon>
            </Tooltip>
            <Tooltip title='Color Picker' arrow>
              <ToolBarIcon onClick={onPressColorPick}>
                <Colorize
                  sx={{
                    fontSize: 20,
                    color:
                      mode === APPLICATION_MODES.PICKER_PICKING ||
                      mode === APPLICATION_MODES.PICKER_PICKED
                        ? '#6366f1'
                        : 'inherit',
                  }}
                />
              </ToolBarIcon>
            </Tooltip>
            {getTopicType() != 'gallery' && (
              <Tooltip title='Border' arrow>
                <ToolBarIcon onClick={onPressGrid}>
                  {bordered ? <GridOn sx={{ fontSize: 20 }} /> : <GridOff sx={{ fontSize: 20 }} />}
                </ToolBarIcon>
              </Tooltip>
            )}
            <Tooltip title='Inspector' arrow>
              <ToolBarIcon onClick={onPressInspector}>
                {debugMode ? (
                  <DeveloperBoard sx={{ fontSize: 20 }} />
                ) : (
                  <DeveloperBoardOff sx={{ fontSize: 20 }} />
                )}
              </ToolBarIcon>
            </Tooltip>
            {/* <Tooltip sx={{height: 40, width: 40, bgcolor: '#ffffff26'}} title="Asset Selector">
          <ToolBarIcon onClick={onPressFullMode}>
            {mode === APPLICATION_MODES.FULL_SCREEN ? <FullscreenExit /> : <Fullscreen />}
          </ToolBarIcon>
        </Tooltip> */}
            {/* {isSliderVisible && (
              <Tooltip
                sx={{ height: 140, width: 200, bgcolor: '#ffffff26' }}
                title={
                  <>
                    <div>Accent Scale: {sliderAccentScale}</div>
                    <div>Accent Number: {sliderAccentNumber}</div>
                    <div>Accent Rotation: {sliderAccentRotation}</div>
                  </>
                }
              >
                <Box display='flex' flexDirection='column' alignItems='center' gap={1}>
                  <Box display='flex' flexDirection='column' alignItems='center' width='100%'>
                    <Box color='white' fontSize='12px' mb={-1}>
                      Accent Scaling
                    </Box>
                    <StyledSlider
                      value={sliderAccentScale}
                      onChange={handleSliderAccentScaleChange}
                      min={0}
                      max={100}
                    />
                  </Box>
                  <Box color='white' fontSize='12px' mt={1} mb={-1}>
                    Accent Tiling
                  </Box>
                  <Box display='flex' flexDirection='column' alignItems='center' width='100%'>
                    <StyledSlider
                      value={sliderAccentNumber}
                      onChange={handleSliderAccentNumberChange}
                      min={1}
                      max={4}
                    />
                  </Box>

                  <Box color='white' fontSize='12px' mt={1} mb={-1}>
                    Accent Rotation
                  </Box>
                  <Box display='flex' flexDirection='column' alignItems='center' width='100%'>
                    <StyledSlider
                      value={sliderAccentRotation} // Bind this to your rotation value
                      onChange={handleSliderRotationNumberChange} // Handle rotation changes
                      min={0}
                      max={360} // For full 0-360 degree rotation
                      step={45} // Step size of 45 degrees
                    />
                  </Box>
                </Box>
              </Tooltip>
            )} */}
          </ToolBarContainer>
        </ToolBarWrapper>
      )}

      {/* <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '60%',
          display: 'flex',
          justifyContent: 'center',
          zIndex: 10,
          bgcolor: '#ffffff26',
          padding: 1,
          borderRadius: 2,
        }}
      >
        <Box display='flex' alignItems='center' width='100%'>
          <Box display='flex' alignItems='center' gap={0}>
            <IconButton onClick={handleCameraZoomOut} sx={{ color: 'white' }}>
              <RemoveIcon />
            </IconButton>
            <IconButton onClick={handleCameraZoomIn} sx={{ color: 'white' }}>
              <AddIcon />
            </IconButton>
          </Box>

          <Box display='flex' flexDirection='column' alignItems='center' gap={1} flex='1' ml={2}>
            <Box color='white' fontSize='12px' mb={-1}>
              Camera Horizontal Position
            </Box>
            <StyledSlider
              value={cameraHorizontalPosition}
              onChange={handleCameraHorizontalPositionChange}
              min={0}
              max={1}
              step={0.01}
              sx={{ width: '100%' }}
            />
          </Box>
        </Box>
      </Box> */}
    </>
  )
})
